import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import { opApi, apiError } from "@/lib/api";
import { Shell, StatCard, money, dateStr } from "@/components/mc/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, LogIn, Building2, Code2, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { InstallSnippet } from "@/components/mc/InstallSnippet";
import { EmbedHealth } from "@/components/mc/EmbedHealth";
import { VerticalsPanel } from "@/components/mc/VerticalsPanel";
import { WidgetsPanel } from "@/components/mc/WidgetsPanel";
import { DeleteShopDialog } from "@/components/mc/DeleteShopDialog";
import { CartControlsSwitch } from "@/components/mc/CartControls";
import { brandPath } from "@/lib/brand";

function StatusBadge({ status }) {
  const map = {
    new: "bg-primary text-white", preparing: "bg-[#FFBD2E] text-black",
    ready: "bg-[hsl(var(--success))] text-white", completed: "bg-secondary text-foreground",
    cancelled: "bg-accent text-white",
  };
  return <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 ${map[status] || "bg-secondary"}`}>{status}</span>;
}

export default function AdminDashboard() {
  const { operator, logout, impersonate } = useOperator();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [crm, setCrm] = useState([]);
  const [loyalty, setLoyalty] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", operator_email: "", primary_color: "#0033A0" });
  const [open, setOpen] = useState(false);
  const [installSlug, setInstallSlug] = useState(null);
  const [deleteShop, setDeleteShop] = useState(null);
  const [healthTenant, setHealthTenant] = useState("");
  const BIZ_TYPES = ["cafe", "restaurant", "food_truck", "retail", "barber", "trainer", "other"];
  const [catBiz, setCatBiz] = useState("cafe");
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");

  const loadCats = useCallback(async (bt) => {
    try { const { data } = await opApi.get(`/admin/categories?business_type=${bt}`); setCats(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c, d, e] = await Promise.all([
        opApi.get("/admin/overview"), opApi.get("/admin/tenants"), opApi.get("/admin/orders"),
        opApi.get("/admin/crm"), opApi.get("/admin/loyalty"),
      ]);
      setOv(a.data); setTenants(b.data); setOrders(c.data); setCrm(d.data); setLoyalty(e.data);
    } catch (err) { /* handled by guard */ }
  }, []);

  useEffect(() => {
    if (operator === false) { navigate(brandPath("/login")); return; }
    if (operator && operator.role !== "superadmin") { navigate(brandPath("/dashboard")); return; }
    if (operator && operator.role === "superadmin") loadAll();
  }, [operator, navigate, loadAll]);

  useEffect(() => {
    if (operator && operator.role === "superadmin") loadCats(catBiz);
  }, [operator, catBiz, loadCats]);

  if (!operator || operator.role !== "superadmin") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const createTenant = async () => {
    setCreating(true);
    try {
      await opApi.post("/admin/tenants", form);
      toast.success("Tenant created");
      setOpen(false);
      setForm({ name: "", slug: "", operator_email: "", primary_color: "#0033A0" });
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const toggleActive = async (t) => {
    try { await opApi.put(`/admin/tenants/${t.id}`, { active: !t.active }); loadAll(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const switchToTenant = async (t) => {
    try { await impersonate(t.id); toast.success(`Viewing as ${t.name}`); navigate(brandPath("/dashboard")); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await opApi.post("/admin/categories", { business_type: catBiz, name, sort_order: cats.length });
      setNewCat(""); loadCats(catBiz); toast.success("Category added");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delCategory = async (c) => {
    try { await opApi.delete(`/admin/categories/${c.id}`); loadCats(catBiz); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const moveCategory = async (c, dir) => {
    const idx = cats.findIndex((x) => x.id === c.id);
    const swap = cats[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        opApi.put(`/admin/categories/${c.id}`, { sort_order: swap.sort_order }),
        opApi.put(`/admin/categories/${swap.id}`, { sort_order: c.sort_order }),
      ]);
      loadCats(catBiz);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Shell badge="SuperAdmin" onLogout={logout}
      right={<span className="text-sm text-muted-foreground hidden sm:inline">{operator.user?.email}</span>}>
      <h1 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl mb-6">Platform Control</h1>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-none bg-secondary border border-border h-auto flex-wrap justify-start">
          {["overview", "tenants", "verticals", "widgets", "categories", "orders", "crm", "loyalty", "health"].map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-none capitalize data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`admin-tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            <StatCard label="Tenants" value={ov?.tenants ?? "—"} sub={`${ov?.active_tenants ?? 0} active`} testid="admin-stat-tenants" />
            <StatCard label="Platform Orders" value={ov?.orders ?? "—"} testid="admin-stat-orders" />
            <StatCard label="CRM Contacts" value={ov?.contacts ?? "—"} testid="admin-stat-contacts" />
            <StatCard label="Gross Revenue" value={ov ? money(ov.revenue) : "—"} accent testid="admin-stat-revenue" />
          </div>
          <h3 className="font-display font-bold text-lg mt-8 mb-3">Recent orders across all tenants</h3>
          <div className="border border-border divide-y divide-border">
            {(ov?.recent_orders || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-muted-foreground">#{o.order_number}</span>
                  <span className="font-medium">{o.customer_name}</span>
                  <StatusBadge status={o.status} />
                </div>
                <span className="font-mono">{money(o.total)}</span>
              </div>
            ))}
            {!ov?.recent_orders?.length && <div className="p-4 text-sm text-muted-foreground">No orders yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="tenants" className="mt-6">
          <CartControlsSwitch />
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">{tenants.length} business{tenants.length !== 1 ? "es" : ""} onboarded</p>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none gap-2" data-testid="add-tenant-btn"><Plus className="h-4 w-4" /> Onboard tenant</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display">Onboard a new business</DialogTitle>
                  <DialogDescription>Create a tenant, choose its subdomain, and optionally assign the operator's MorrAccess email.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>Business name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name-input" placeholder="Joe's Barbershop" /></div>
                  <div><Label>Subdomain slug</Label>
                    <div className="flex items-center mt-1">
                      <Input className="rounded-none" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })} data-testid="tenant-slug-input" placeholder="joesbarber" />
                      <span className="ml-2 text-sm text-muted-foreground font-mono">.morrcart.com</span>
                    </div>
                  </div>
                  <div><Label>Operator email (MorrAccess)</Label><Input className="rounded-none mt-1" value={form.operator_email} onChange={(e) => setForm({ ...form, operator_email: e.target.value })} data-testid="tenant-operator-input" placeholder="owner@business.com" /></div>
                  <div><Label>Brand color</Label><Input type="color" className="rounded-none mt-1 h-10 w-20 p-1" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="tenant-color-input" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={createTenant} disabled={creating || !form.name || !form.slug} className="rounded-none" data-testid="create-tenant-submit">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create tenant"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 border-t border-l border-border">
            {tenants.map((t) => (
              <div key={t.id} className="border-r border-b border-border p-5" data-testid={`tenant-card-${t.slug}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 flex items-center justify-center text-white" style={{ background: t.primary_color }}><Building2 className="h-4 w-4" /></div>
                    <div>
                      <div className="font-display font-bold">{t.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{t.slug}.morrcart.com</div>
                    </div>
                  </div>
                  <Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`tenant-active-${t.slug}`} />
                </div>
                <div className="mt-4 flex gap-4 text-sm">
                  <span><span className="font-mono font-bold">{t.order_count}</span> <span className="text-muted-foreground">orders</span></span>
                  <span><span className="font-mono font-bold">{t.customer_count}</span> <span className="text-muted-foreground">customers</span></span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-none gap-2 flex-1" onClick={() => switchToTenant(t)} data-testid={`impersonate-${t.slug}`}>
                    <LogIn className="h-4 w-4" /> Switch to tenant
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none gap-2" onClick={() => setInstallSlug(t.slug)} data-testid={`install-code-${t.slug}`}>
                    <Code2 className="h-4 w-4" /> Install code
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-none text-destructive hover:text-destructive" onClick={() => setDeleteShop(t)} data-testid={`delete-shop-${t.slug}`} aria-label={`Delete ${t.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DeleteShopDialog key={deleteShop?.id} shop={deleteShop} onClose={() => setDeleteShop(null)} onDeleted={() => { setDeleteShop(null); loadAll(); }} />
          <Dialog open={!!installSlug} onOpenChange={(v) => !v && setInstallSlug(null)}>
            <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Install code — {installSlug}</DialogTitle>
                <DialogDescription>Copy-paste snippet to add the cart & customer portal to this tenant's website.</DialogDescription>
              </DialogHeader>
              {installSlug && <InstallSnippet slug={installSlug} />}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:font-medium [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                <th>Order</th><th>Tenant</th><th>Customer</th><th>Fulfillment</th><th>Status</th><th className="text-right">Total</th><th>Placed</th></tr></thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => (
                  <tr key={o.id} className="[&>td]:px-4 [&>td]:py-2.5" data-testid={`admin-order-${o.order_number}`}>
                    <td className="font-mono">#{o.order_number}</td><td>{o.tenant_name}</td><td>{o.customer_name}</td>
                    <td className="text-muted-foreground">{o.fulfillment === "pickup_later" ? "Later" : "Now"}</td>
                    <td><StatusBadge status={o.status} /></td><td className="text-right font-mono">{money(o.total)}</td>
                    <td className="text-muted-foreground text-xs">{dateStr(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!orders.length && <div className="p-4 text-sm text-muted-foreground">No orders.</div>}
          </div>
        </TabsContent>

        <TabsContent value="crm" className="mt-6">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr className="[&>th]:px-4 [&>th]:py-2 [&>th]:font-medium [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                <th>Name</th><th>Email</th><th>Tenant</th><th className="text-right">Orders</th><th className="text-right">Spent</th></tr></thead>
              <tbody className="divide-y divide-border">
                {crm.map((c) => (
                  <tr key={c.id} className="[&>td]:px-4 [&>td]:py-2.5" data-testid={`admin-crm-${c.id}`}>
                    <td className="font-medium">{c.name}</td><td className="text-muted-foreground">{c.email}</td><td>{c.tenant_name}</td>
                    <td className="text-right font-mono">{c.order_count}</td><td className="text-right font-mono">{money(c.total_spent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!crm.length && <div className="p-4 text-sm text-muted-foreground">No contacts.</div>}
          </div>
        </TabsContent>

        <TabsContent value="loyalty" className="mt-6">
          <p className="text-sm text-muted-foreground mb-4">Platform-wide loyalty memberships. To create or edit programs, use “Switch to tenant”.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {loyalty.map((l) => (
              <div key={l.id} className="border-r border-b border-border p-5" data-testid={`admin-loyalty-${l.id}`}>
                <div className="font-display font-bold">{l.customer_name}</div>
                <div className="text-xs text-muted-foreground">{l.tenant_name} · {l.program_title}</div>
                <div className="mt-3 flex items-end gap-4">
                  <div><div className="font-mono font-bold text-2xl text-primary">{l.progress}<span className="text-sm text-muted-foreground">/{l.goal}</span></div><div className="text-xs text-muted-foreground uppercase tracking-wider">progress</div></div>
                  <div><div className="font-mono font-bold text-2xl">{l.rewards_ready || 0}</div><div className="text-xs text-muted-foreground uppercase tracking-wider">ready</div></div>
                </div>
                {l.reward && <div className="mt-2 text-xs text-muted-foreground">Reward: {l.reward}</div>}
              </div>
            ))}
            {!loyalty.length && <div className="p-4 text-sm text-muted-foreground border-r border-b border-border">No loyalty records.</div>}
          </div>
        </TabsContent>

        <TabsContent value="verticals" className="mt-6">
          <VerticalsPanel onSwitch={switchToTenant} />
        </TabsContent>

        <TabsContent value="widgets" className="mt-6">
          <WidgetsPanel tenants={tenants} />
        </TabsContent>

        <TabsContent value="health" className="mt-6">
          <h2 className="font-display font-bold text-xl">Embed health check</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Proactively verify the full path — MorrAccess → MorrCart → the tenant's website — so install issues surface before customers complain.</p>
          <div className="max-w-2xl mb-4">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Tenant</label>
            <select value={healthTenant} onChange={(e) => setHealthTenant(e.target.value)} data-testid="health-tenant-select"
              className="mt-1 w-full h-10 rounded-none border border-border bg-background px-3 text-sm">
              <option value="">Select a tenant…</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>
          {healthTenant && <EmbedHealth endpoint="/admin/embed-health" tenantId={healthTenant} />}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <h2 className="font-display font-bold text-xl">Menu categories</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Define the predefined categories that tenants pick from for each business type. Tenants can still add a custom category per product.</p>
          <div className="max-w-xl">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Business type</label>
            <select value={catBiz} onChange={(e) => setCatBiz(e.target.value)} data-testid="category-biztype-select"
              className="mt-1 mb-5 w-full h-10 rounded-none border border-border bg-background px-3 text-sm capitalize">
              {BIZ_TYPES.map((b) => <option key={b} value={b}>{b.replace("_", " ")}</option>)}
            </select>

            <div className="border-t border-l border-border" data-testid="admin-categories-list">
              {cats.map((c, i) => (
                <div key={c.id} className="border-r border-b border-border px-3 py-2 flex items-center gap-2" data-testid={`admin-category-${c.id}`}>
                  <span className="font-mono text-[10px] text-muted-foreground w-5">{i + 1}</span>
                  <span className="flex-1 text-sm font-medium">{c.name}</span>
                  <button onClick={() => moveCategory(c, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`category-up-${c.id}`}><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => moveCategory(c, 1)} disabled={i === cats.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`category-down-${c.id}`}><ChevronDown className="h-4 w-4" /></button>
                  <button onClick={() => delCategory(c)} className="text-muted-foreground hover:text-accent" data-testid={`delete-category-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {!cats.length && <div className="border-r border-b border-border px-3 py-3 text-sm text-muted-foreground">No categories yet for this business type.</div>}
            </div>

            <div className="flex gap-2 mt-4">
              <Input className="rounded-none" placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }} data-testid="new-category-input" />
              <Button onClick={addCategory} disabled={!newCat.trim()} className="rounded-none gap-2" data-testid="add-category-btn"><Plus className="h-4 w-4" /> Add</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
